import { parseRequestError } from '@/common/utilities.ts'
import axios from 'axios'
import { z } from 'zod'
import { Task, TaskSchema } from './tasks.schema.ts'

export const FamilyApplicationPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(TaskSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.union([
      z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
      z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
    ])
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export type FilterStatusOption = number[]

export type TaskList = Task[]

export type TaskSortBy = {
  submitted_at: 'asc' | 'desc'
}

export type PaginatedTasks = z.infer<typeof FamilyApplicationPaginatedSchema>
export const getPaginatedTasks = async function (
  currentPage: number,
  recordsPerPage: number = 20,
  search?: string | undefined,
  task_type_id?: number,
  sort?: TaskSortBy,
  program_id?: number
): Promise<PaginatedTasks> {
  try {
    const sort_col = sort
      ? (Object.keys(sort)[0] as keyof TaskSortBy)
      : undefined
    const sort_order = sort && sort_col ? sort[sort_col] : undefined

    const params: {
      paginate: number
      per_page: number
      page: number
      search?: string
      task_type_id?: number
      sort_by?: string
      sort_order?: string
      program_id?: number
    } = {
      paginate: 1,
      per_page: recordsPerPage,
      page: currentPage,
      ...(search && { search }),
      ...(task_type_id && { task_type_id }),
      ...(sort_col && { sort_by: sort_col }),
      ...(sort && { sort_order }),
      ...(program_id && { program_id }),
    }
    const response = await axios.get('/tasks', {
      params,
    })

    if (response.data.message === 'No Task found') {
      return {
        current_page: currentPage,
        data: [],
        first_page_url: '',
        from: 0,
        last_page: 0,
        last_page_url: '',
        links: [],
        next_page_url: null,
        path: '',
        per_page: recordsPerPage,
        prev_page_url: null,
        to: 0,
        total: 0,
      }
    }

    return FamilyApplicationPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

const ApplicationFlagObjectSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const ApplicationFlagListSchema = z.object({
  data: z.array(ApplicationFlagObjectSchema),
})
export type ApplicationFlagList = z.infer<typeof ApplicationFlagListSchema>

export const GetApplicationFlagList =
  async (): Promise<ApplicationFlagList> => {
    try {
      const response = await axios.get(`global_application_flags`)
      return ApplicationFlagListSchema.parse(response.data)
    } catch (e) {
      throw new Error(parseRequestError(e).message)
    }
  }
